import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Database, ListOrdered, CreditCard, ChefHat, Target, Folder, FileText, ChevronsUpDown, Check, Truck, Tag } from "lucide-react";
import { CategoriaProdutoTable } from "@/components/tabelas/CategoriaProdutoTable";
import { TipoMovimentoTable } from "@/components/tabelas/TipoMovimentoTable";
import { TipoDocumentoTable } from "@/components/tabelas/TipoDocumentoTable";
import { FormaPagamentoTable } from "@/components/tabelas/FormaPagamentoTable";
import { CategoriaReceitaTable } from "@/components/tabelas/CategoriaReceitaTable";
import { NivelDificuldadeTable } from "@/components/tabelas/NivelDificuldadeTable";
import { FornecedorTable } from "@/components/tabelas/FornecedorTable";
import { MarcaTable } from "@/components/tabelas/MarcaTable";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Definição das tabelas auxiliares
const tabelasAuxiliares = [
  {
    categoria: "Produtos e Estoque",
    tabelas: [
      {
        id: "categoria-produto",
        nome: "Categorias de Produtos",
        descricao: "Categorias utilizadas no cadastro de materiais (farinha, açúcar, etc.)",
        icon: Folder,
        component: CategoriaProdutoTable,
      },
      {
        id: "fornecedor",
        nome: "Fornecedores",
        descricao: "Fornecedores de materiais e ingredientes",
        icon: Truck,
        component: FornecedorTable,
      },
      {
        id: "marca",
        nome: "Marcas",
        descricao: "Marcas de produtos e ingredientes",
        icon: Tag,
        component: MarcaTable,
      },
      {
        id: "tipo-movimento",
        nome: "Tipos de Movimento",
        descricao: "Tipos de movimentação de estoque (compra, bonificação, etc.)",
        icon: ListOrdered,
        component: TipoMovimentoTable,
      },
      {
        id: "tipo-documento",
        nome: "Tipos de Documento",
        descricao: "Tipos de documentos fiscais e comprovantes (NFe, Cupom, etc.)",
        icon: FileText,
        component: TipoDocumentoTable,
      },
    ],
  },
  {
    categoria: "Vendas e Finanças",
    tabelas: [
      {
        id: "forma-pagamento",
        nome: "Formas de Pagamento",
        descricao: "Formas de pagamento utilizadas em entradas e vendas",
        icon: CreditCard,
        component: FormaPagamentoTable,
      },
    ],
  },
  {
    categoria: "Receitas",
    tabelas: [
      {
        id: "categoria-receita",
        nome: "Categorias de Receitas",
        descricao: "Categorias utilizadas no cadastro de receitas (bolos, tortas, etc.)",
        icon: ChefHat,
        component: CategoriaReceitaTable,
      },
      {
        id: "dificuldade",
        nome: "Níveis de Dificuldade",
        descricao: "Níveis de dificuldade para receitas (fácil, média, difícil)",
        icon: Target,
        component: NivelDificuldadeTable,
      },
    ],
  },
];

// Flatten all tables for easy lookup
const allTables = tabelasAuxiliares.flatMap((cat) => cat.tabelas);

export default function Tabelas() {
  const [open, setOpen] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState(allTables[0].id);

  const selectedTable = allTables.find((t) => t.id === selectedTableId);
  const SelectedComponent = selectedTable?.component;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Database className="w-8 h-8" />
            Tabelas Auxiliares
          </h1>
          <p className="text-muted-foreground">
            Gerencie categorias, tipos e outras tabelas de domínio do sistema
          </p>
        </div>

        {/* ComboBox Selector */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <label className="text-sm font-medium">Selecione a tabela:</label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full sm:w-[400px] justify-between"
              >
                <div className="flex items-center gap-2">
                  {selectedTable && <selectedTable.icon className="h-4 w-4" />}
                  {selectedTable?.nome || "Selecione uma tabela..."}
                </div>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full sm:w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar tabela..." />
                <CommandList>
                  <CommandEmpty>Nenhuma tabela encontrada.</CommandEmpty>
                  {tabelasAuxiliares.map((categoria) => (
                    <CommandGroup key={categoria.categoria} heading={categoria.categoria}>
                      {categoria.tabelas.map((tabela) => (
                        <CommandItem
                          key={tabela.id}
                          value={tabela.nome}
                          onSelect={() => {
                            setSelectedTableId(tabela.id);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedTableId === tabela.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <tabela.icon className="mr-2 h-4 w-4" />
                          <span>{tabela.nome}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Selected Table Content */}
        {selectedTable && (
          <Card className="animate-in fade-in-50 duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <selectedTable.icon className="h-5 w-5" />
                {selectedTable.nome}
              </CardTitle>
              <CardDescription>{selectedTable.descricao}</CardDescription>
            </CardHeader>
            <CardContent>
              <SelectedComponent />
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
